import {
  LevelsConfigAttributes,
  SetLevelConfigBody,
} from '@loyalty/shared-common/gamification-api-client';
import { QlfPlaceholder, useAlerts } from '@qualifio/ui-commons';
import { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { Skeleton } from '@mui/material';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { QlfPlaceholderIcon } from '../shared/qlf-placeholder-icon/QlfPlaceholderIcon';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';
import { useSelectiveEffect } from '../shared/use-selective-effect';

interface ILevelConfigContext {
  levelsConfig: LevelsConfigAttributes | undefined;
  error: any;
  setLevelConfig: (levelConfig: SetLevelConfigBody) => Promise<boolean>;
  getLevelConfig: () => void;
  isLoading: IsLoading;
}

const LevelConfigContext = createContext<ILevelConfigContext>({} as any);

export const useLevelsConfig = () => useContext(LevelConfigContext);

export function LevelConfigProvider({ children }: { children: ReactNode }) {
  const { managerApi, gamificationApi } = useApi();
  const { program } = useProgram();
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const [levelsConfig, setLevelsConfig] = useState<LevelsConfigAttributes | undefined>();
  const [error, setError] = useState();

  const { addLoader, removeLoader, isLoading } = useLoaders({ getLevelConfig: true });

  const setLevelConfig = useCallback(
    async (levelConfig: SetLevelConfigBody) => {
      addLoader('setLevelConfig');

      return managerApi
        .setLevelsConfig(program.id, levelConfig)
        .then(levelConfig => {
          setLevelsConfig(levelConfig);
          return true;
        })
        .catch(error => {
          notify(t('levels.errors.set-config', { error }), 'error');
          setError(error);
          return false;
        })
        .finally(() => removeLoader('setLevelConfig'));
    },
    [addLoader, removeLoader, managerApi, program.id, notify, t],
  );

  const getLevelConfig = useCallback(() => {
    addLoader('getLevelConfig');

    gamificationApi
      .getLevelConfig(program.id)
      .then(config => setLevelsConfig(config ?? undefined))
      .catch(setError)
      .finally(() => removeLoader('getLevelConfig'));
  }, [addLoader, removeLoader, gamificationApi, program.id]);

  useSelectiveEffect(
    () => {
      getLevelConfig();
    },
    [getLevelConfig, program.id],
    [program.id],
  );

  const context = { levelsConfig, setLevelConfig, getLevelConfig, isLoading, error };

  return isLoading('getLevelConfig') ? (
    <Skeleton />
  ) : error ? (
    <QlfPlaceholder
      icon={<QlfPlaceholderIcon icon={faTimes} />}
      primaryText={t('levels.errors.load-level-config.title')}
      secondaryText={t('levels.errors.load-level-config.message')}
    />
  ) : (
    <LevelConfigContext.Provider value={context}> {children}</LevelConfigContext.Provider>
  );
}
